'use client'

import { useState, useEffect, useRef } from 'react'
import Sidebar from './Sidebar'
import Header from './Header'
import BottomNav from './BottomNav'
import { NotificationsProvider } from '@/contexts/NotificationsContext'
import { Loading } from '@/components/ui/loading'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ProtectedRoute } from '@/guards/ProtectedRoute'
import { cn } from '@/lib/utils'
import { LayoutProvider } from '@/contexts/LayoutContext'

// Global types
declare global {
  interface Window {
    forceDashboardRender?: boolean;
    mockAuthUser?: any;
  }
}

interface MainLayoutProps {
  children: React.ReactNode
  title?: string
}

const HEADER_HEIGHT = 60; // Define header height constant
const SCROLL_THRESHOLD = 5; // Reduced threshold to be more sensitive to scroll

export default function MainLayout({ children, title }: MainLayoutProps) {
  const [isClient, setIsClient] = useState(false) // Track if we're on client
  const [showHeader, setShowHeader] = useState(true); // State for header visibility
  const [lastScrollY, setLastScrollY] = useState(0); // State for last scroll position
  const mainScrollRef = useRef<HTMLElement>(null); // Ref for the main scrollable element

  // Mark when we're hydrated on client
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Effect to handle scroll events for header visibility on mobile
  useEffect(() => {
    if (!isClient || !mainScrollRef.current) return; // Only run on client with ref

    const scrollElement = mainScrollRef.current;

    const handleScroll = () => {
      const currentScrollY = scrollElement.scrollTop;
      const isMobile = window.innerWidth < 768; // md breakpoint
      
      // Skip minimal scroll changes but use a smaller threshold
      if (Math.abs(currentScrollY - lastScrollY) < SCROLL_THRESHOLD) return;
      
      if (!isMobile) {
        // Ensure header is shown and exit if on desktop
        if (!showHeader) setShowHeader(true); 
        return;
      }

      // Improved mobile scroll logic
      if (currentScrollY > lastScrollY && currentScrollY > 20) {
        // Scrolling Down - hide header
        setShowHeader(false);
      } else if (currentScrollY < lastScrollY || currentScrollY <= 20) {
        // Scrolling Up or at the top - show header
        setShowHeader(true);
      }

      setLastScrollY(currentScrollY <= 0 ? 0 : currentScrollY); // Update last scroll position
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollElement.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', onScroll);
    };
  }, [isClient, lastScrollY, showHeader]); // Rerun effect if these change

  // For initial server render and client pre-hydration, show a loading indicator
  if (!isClient) {
    return (
      <div className="flex min-h-screen">
        <div className="w-full flex items-center justify-center">
          <Loading size="lg" text="Loading..." />
        </div>
      </div>
    );
  }

  // Wrap the main content area with ProtectedRoute
  return (
    <ErrorBoundary>
      <ProtectedRoute>
        <LayoutProvider value={{ mainScrollRef }}>
          <div className="flex h-screen flex-col md:flex-row overflow-hidden">
            <div className="hidden md:flex md:flex-shrink-0">
              <Sidebar />
            </div>

            <div className="flex flex-1 flex-col overflow-hidden border-l border-gray-800 pt-[60px] md:pt-[60px] min-w-0">
              <Header 
                onToggleSidebar={() => {}} 
                title={title} 
                showHeader={showHeader}
              />

              <main 
                ref={mainScrollRef} 
                className="flex-1 overflow-y-auto bg-white p-4 pt-1 md:p-6 pb-[80px] md:pb-6"
                style={{ overscrollBehavior: 'none' }}
              >
                {children}
              </main>
            </div>

            <BottomNav />
          </div>
        </LayoutProvider>
      </ProtectedRoute>
    </ErrorBoundary>
  )
} 